/**
 * LE SIGNAL DES ECRITURES : « une fiche de cette famille vient de changer ».
 *
 * POURQUOI IL EXISTE. Un ecran qui montre un total (pastille du menu, stat
 * d'en-tete) le tient d'un chargement. Tant que c'est LUI qui ecrit, il recharge
 * apres son geste et tout suit. Mais une surface d'app (porte Canvas) ecrit
 * DIRECTEMENT par le SDK : personne ne dit a l'ecran generique que le total a
 * bouge, et le chiffre reste fige jusqu'au rechargement de la page. Le SDK est
 * le seul endroit par lequel passent TOUTES les ecritures, quel que soit
 * l'appelant : c'est donc lui qui le dit.
 *
 * CE QUE C'EST, ET CE QUE CE N'EST PAS. Un signal en memoire, dans ce process,
 * pour cet onglet. Rien de persistant, rien de reseau : une ecriture faite par
 * un autre poste ne passe pas ici (c'est le travail des lectures temps reel).
 * Le signal dit QU'une ecriture a reussi, jamais ce qu'elle contenait : l'abonne
 * relit ce dont il a besoin.
 */

/** Les cinq verbes d'ecriture d'une famille. */
export type GesteDEcriture = 'create' | 'update' | 'archive' | 'unarchive' | 'remove';

/** Une ecriture REUSSIE : la famille (au pluriel, la clef du SDK), le geste, la fiche. */
export interface EcritureSignalee {
  famille: string;
  geste: GesteDEcriture;
  id: string;
}

/** Ce que l'on peut faire du signal : s'y abonner, et se desabonner. */
export interface SignalDesEcritures {
  /** Rend la fonction de desabonnement. */
  observer(rappel: (ecriture: EcritureSignalee) => void): () => void;
}

/** Le signal, plus le cote reserve au SDK : emettre. */
export interface SignalEmetteur extends SignalDesEcritures {
  emettre(ecriture: EcritureSignalee): void;
}

/**
 * LA MARQUE d'un verbe enveloppe. Elle sert a deux choses : ne JAMAIS envelopper
 * deux fois (le signal partirait en double), et permettre a un test de prouver
 * que chaque famille du SDK est couverte sans ecrire dans une base.
 */
const MARQUE_D_ENVELOPPE = Symbol.for('taki.sdk.ecritures.enveloppe');

/** Ce verbe a-t-il ete enveloppe par `envelopperLesEcritures` ? */
export function estEnveloppe(verbe: unknown): boolean {
  return typeof verbe === 'function' && (verbe as unknown as Record<symbol, unknown>)[MARQUE_D_ENVELOPPE] === true;
}

export const GESTES_D_ECRITURE: readonly GesteDEcriture[] = [
  'create',
  'update',
  'archive',
  'unarchive',
  'remove',
];

/**
 * LA FABRIQUE DU SIGNAL. Un abonne qui jette ne casse NI l'ecriture (elle a deja
 * reussi quand on emet) NI les autres abonnes : chacun est isole, l'erreur est
 * dite en console et le tour continue. Les abonnes sont copies avant l'appel,
 * pour qu'un abonne qui se desabonne pendant l'emission ne decale pas la boucle.
 */
export function creerSignalDesEcritures(): SignalEmetteur {
  const abonnes = new Set<(ecriture: EcritureSignalee) => void>();
  return {
    observer(rappel) {
      abonnes.add(rappel);
      return () => {
        abonnes.delete(rappel);
      };
    },
    emettre(ecriture) {
      for (const rappel of [...abonnes]) {
        try {
          rappel(ecriture);
        } catch (erreur) {
          console.error(`Un abonne au signal des ecritures a leve (${ecriture.famille}, ${ecriture.geste}).`, erreur);
        }
      }
    },
  };
}

/**
 * ENVELOPPE, EN PLACE, LES CINQ VERBES d'un repository de famille. Le verbe
 * d'origine est appele tel quel (meme `this`, memes arguments) ; le signal part
 * APRES son succes seulement : une ecriture refusee (validation, rules) n'a rien
 * change, annoncer un changement ferait recompter pour rien. Le resultat et les
 * erreurs repassent intacts.
 *
 * EN PLACE, et pas une copie : `sdk.timesheets`, `sdk.famille('timesheets')`,
 * `sdk.createIn` et `sdk.repositoryOf` lisent TOUS le meme objet, et un modele
 * (`templates.instantiate`) cree par la table des familles. Une copie laisserait
 * un chemin qui ecrit sans le dire.
 *
 * Un verbe absent (ou qui n'est pas une fonction) est laisse tel quel : toutes
 * les familles n'ont pas forcement les cinq.
 */
export function envelopperLesEcritures(
  famille: string,
  repository: Record<string, unknown>,
  signal: Pick<SignalEmetteur, 'emettre'>,
): void {
  for (const geste of GESTES_D_ECRITURE) {
    const origine = repository[geste];
    if (typeof origine !== 'function' || estEnveloppe(origine)) continue;
    const enveloppe = async function (this: unknown, ...args: unknown[]): Promise<unknown> {
      const resultat: unknown = await (origine as (...a: unknown[]) => unknown).apply(
        this ?? repository,
        args,
      );
      const id = geste === 'create' ? identifiantDuResultat(resultat) : String(args[0] ?? '');
      signal.emettre({ famille, geste, id });
      return resultat;
    };
    Object.defineProperty(enveloppe, MARQUE_D_ENVELOPPE, { value: true });
    repository[geste] = enveloppe;
  }
}

function identifiantDuResultat(resultat: unknown): string {
  if (resultat != null && typeof resultat === 'object') {
    const id = (resultat as { id?: unknown }).id;
    if (typeof id === 'string') return id;
  }
  return '';
}
